import React, { useEffect, useState } from "react";

import { IconChevronDown, IconChevronUp } from "@frontend/common";
import * as Sentry from "@sentry/react";

import { useAIAdvisorQuery } from "@api/aiAdvisor/aiAdvisor";
import { showToast } from "@atoms/Toast/Toast";
import { getPressIdFromURL } from "@utils/getStateFromURL";

import * as S from "./AIAdvisorBottomSheet.style";
import AIAdvisorTable from "./components/AIAdvisorTable/AIAdvisorTable";

const AIAdvisorBottomSheet = () => {
  const draftArticleId = getPressIdFromURL();

  const { data: aiAdvisor, isError, error } = useAIAdvisorQuery(draftArticleId);

  const [isOpen, setIsOpen] = useState(true);

  const changeOpenStatus = (newStatus: boolean) => {
    setIsOpen(newStatus);
  };

  useEffect(() => {
    if (isError) {
      showToast({
        icon: "error",
        backgroundColor: "hotPink",
        text: "Failed to get AI Advisor Data",
        position: "topCenter",
        autoClose: 5000,
      });

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const axiosError = error as any;
      const statusCode = axiosError?.response?.status || axiosError?.status;

      if (statusCode !== 401) {
        Sentry.captureMessage(`[#${draftArticleId}] AI Advisor Error`, {
          level: "error",
          tags: {
            pressId: draftArticleId,
            type: "ai-advisor-error",
          },
          extra: {
            title: `[#${draftArticleId}] AI Advisor Error`,
          },
        });
      }
    }
  }, [draftArticleId, isError, error]);

  const totalCount = aiAdvisor?.data?.aiAdviceData?.length ?? 0;

  if (isError || aiAdvisor?.code !== 200) {
    return (
      <S.FixedLayout $isOpen={false}>
        <S.CloseButton disabled>AI Advisor (0)</S.CloseButton>
      </S.FixedLayout>
    );
  }

  if (!aiAdvisor?.data?.adviceDt || totalCount === 0) {
    return (
      <S.FixedLayout $isOpen={false}>
        <S.CloseButton disabled>
          <IconChevronUp width={20} height={20} color="#96A1B0" />
          AI Advisor ({totalCount})
        </S.CloseButton>
      </S.FixedLayout>
    );
  }

  return (
    <S.FixedLayout $isOpen={isOpen}>
      {!isOpen && (
        <S.CloseButton onClick={() => changeOpenStatus(true)}>
          <IconChevronUp width={20} height={20} color="#FFFFFF" />
          AI Advisor ({totalCount})
        </S.CloseButton>
      )}
      {isOpen && (
        <S.BottomSheetWrapper>
          <S.OpenButton onClick={() => changeOpenStatus(false)}>
            <IconChevronDown width={20} height={20} color="#0198e9" />
            AI Advisor ({totalCount})
          </S.OpenButton>
          <S.BottomSheet id="ai-advisor-bottom-sheet">
            <AIAdvisorTable aiAdvisorData={aiAdvisor.data} />
          </S.BottomSheet>
        </S.BottomSheetWrapper>
      )}
    </S.FixedLayout>
  );
};

export default AIAdvisorBottomSheet;
